interface SummaryBlock {
  type: "heading" | "paragraph" | "bullets";
  text?: string;
  items?: string[];
}

export function parseJobSummary(raw: string): SummaryBlock[] {
  if (!raw) return [];

  // Split on bullet character — first chunk is prose, rest are bullet items
  const parts = raw.split("•").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return [];

  const prose = parts[0];
  const bulletItems = parts.slice(1);

  const blocks: SummaryBlock[] = [];

  // Split prose by section headings: a capitalised phrase (≤5 words) followed by ": "
  // We match word boundaries instead of lookbehind for broad browser/Babel compat.
  const headingRe = /\b([A-Z][A-Za-z &]{0,40}:)\s/g;
  let cursor = 0;
  let m: RegExpExecArray | null;

  while ((m = headingRe.exec(prose)) !== null) {
    const candidate = m[1];
    const wordCount = candidate.replace(/ ?:$/, "").trim().split(/\s+/).length;
    if (wordCount > 5) {
      // Not a real section heading — emit accumulated text as prose and advance
      const body = prose.slice(cursor, m.index + m[0].length).trim();
      if (body) blocks.push({ type: "paragraph", text: body });
      cursor = m.index + m[0].length;
      continue;
    }

    const body = prose.slice(cursor, m.index).trim();
    if (body) blocks.push({ type: "paragraph", text: body });
    blocks.push({ type: "heading", text: candidate });
    cursor = m.index + m[0].length;
  }
  const tail = prose.slice(cursor).trim();
  if (tail) blocks.push({ type: "paragraph", text: tail });

  if (bulletItems.length > 0) {
    // If the last prose block ends with a heading phrase, extract it
    const last = blocks[blocks.length - 1];
    if (last?.type === "paragraph") {
      const trailRe = /^([\s\S]*?)\s*\b([A-Z][A-Za-z &]{0,40}:)\s*$/;
      const t = last.text!.match(trailRe);
      if (t && t[2].replace(/ ?:$/, "").trim().split(/\s+/).length <= 5) {
        last.text = t[1].trim();
        if (!last.text) blocks.pop();
        blocks.push({ type: "heading", text: t[2] });
      }
    }

    // Scan each bullet item for an embedded section heading at its end
    // (e.g. "...code quality. Required Skills: " → split the heading out)
    const trailRe = /^([\s\S]+?)\s+([A-Z][A-Za-z &]{0,40}:)\s*$/;
    const currentGroup: string[] = [];
    for (const item of bulletItems) {
      const t = item.match(trailRe);
      if (t && t[2].replace(/ ?:$/, "").trim().split(/\s+/).length <= 5) {
        if (t[1].trim()) currentGroup.push(t[1].trim());
        if (currentGroup.length) {
          blocks.push({ type: "bullets", items: [...currentGroup] });
          currentGroup.length = 0;
        }
        blocks.push({ type: "heading", text: t[2] });
      } else {
        currentGroup.push(item);
      }
    }
    if (currentGroup.length) blocks.push({ type: "bullets", items: currentGroup });
  }

  return blocks.filter(
    (b) => (b.type === "bullets" && b.items!.length > 0) || (b.text && b.text.length > 0),
  );
}

export function daysAgo(dateStr?: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function formatPostedDate(dateStr?: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

export const EMPLOYMENT_COLORS: Record<string, string> = {
  "Full-time": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  "Part-time": "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  Contract: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  Internship: "bg-pink-500/10 text-pink-400 border-pink-500/20",
};
